/**
 * `<facet-filters-form>` custom element (FR12, FR15).
 *
 * Handles sort and filter changes by navigating to the correct URL while
 * preserving tracking parameters (`utm_*`, `gclid`, `fbclid`, `_ga*`) already
 * present in the address bar (AD-27). Filter checkboxes carry the URLs
 * Shopify computed for them: `data-url-to-add` when ticked, `data-url-to-remove`
 * when unticked — a ticked value navigated to "add" never came off (review of
 * 22/08, #10).
 *
 * Before navigating it emits the standard event for its context (AD-29):
 * `shopify:collection:update` on a collection, `shopify:search:update` on the
 * search results page — the page reloads, so the event carries what the
 * customer asked for (sort and active filters), not the new result set.
 */
class FacetFiltersForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-facets-form]');
    this.form.addEventListener('change', this.onChange.bind(this));

    /* Sidebar (v1.2): i gruppi arrivano aperti dal Liquid; su schermo
       stretto tutto parte chiuso dietro il disclosure "Filtri". Il listener
       su matchMedia copre la finestra che si stringe DOPO il caricamento e
       l'anteprima mobile dell'editor (26/08): al solo load non bastava. */
    this.mq = window.matchMedia('(max-width: 749px)');
    this.applyViewportState = this.applyViewportState.bind(this);
    this.applyViewportState();
    this.mq.addEventListener('change', this.applyViewportState);
  }

  disconnectedCallback() {
    if (this.mq) this.mq.removeEventListener('change', this.applyViewportState);
  }

  applyViewportState() {
    const wrap = this.querySelector('[data-facets-filters]');
    if (this.mq.matches) {
      this.querySelectorAll('[data-filter-group][open]').forEach((group) => {
        group.removeAttribute('open');
      });
      if (wrap) wrap.removeAttribute('open');
    } else if (wrap) {
      // tornati larghi: il summary sparisce, il contenuto deve rivedersi
      wrap.setAttribute('open', '');
    }
  }

  onChange(event) {
    const target = event.target;
    if (target.matches('[data-filter-value]')) {
      this.navigate(target.checked ? target.dataset.urlToAdd : target.dataset.urlToRemove);
    } else if (target.matches('[data-sort]')) {
      this.updateParam('sort_by', target.value);
    } else if (target.matches('input[type="number"]')) {
      this.updateParam(target.name, target.value);
    }
  }

  updateParam(name, value) {
    const url = new URL(window.location.href);
    if (value) {
      url.searchParams.set(name, value);
    } else {
      url.searchParams.delete(name);
    }
    url.searchParams.delete('page');
    this.navigate(url.toString());
  }

  /** Sort and active filters as the form holds them now. */
  state() {
    const sort = this.form.querySelector('[data-sort]');
    const filters = [];
    this.form.querySelectorAll('[data-filter-value]:checked').forEach((input) => {
      filters.push({ param: input.name, value: input.value });
    });
    this.form.querySelectorAll('input[type="number"]').forEach((input) => {
      if (input.value) filters.push({ param: input.name, value: input.value });
    });
    return { sortBy: sort ? sort.value : null, filters };
  }

  dispatchUpdate() {
    const { sortBy, filters } = this.state();
    if (this.dataset.context === 'search') {
      window.THCabina.events.dispatch('shopify:search:update', {
        terms: this.dataset.searchTerms || '',
        sortBy,
        filters
      });
    } else {
      window.THCabina.events.dispatch('shopify:collection:update', {
        context: 'collection',
        collection: { id: this.dataset.collectionId, handle: this.dataset.collectionHandle },
        sortBy,
        filters
      });
    }
  }

  navigate(urlString) {
    if (!urlString) return;
    const target = new URL(urlString, window.location.origin);
    const current = new URLSearchParams(window.location.search);
    current.forEach((value, key) => {
      if (key.startsWith('utm_') || key.startsWith('_ga') || key === 'fbclid' || key === 'gclid') {
        if (!target.searchParams.has(key)) {
          target.searchParams.set(key, value);
        }
      }
    });
    this.dispatchUpdate();
    window.location.href = target.toString();
  }
}

if (!customElements.get('facet-filters-form')) {
  customElements.define('facet-filters-form', FacetFiltersForm);
}
